import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Shop } from '@components/ui/shop/shop';
import { faLink, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackApiAdapter } from '../../adapters/PackApiAdapter';
import { ShopApiAdapter } from '../../adapters/ShopApiAdapter';
import { Pack } from '../../models/Pack';

export interface LinkPackModalRef {
  openWithPack: (pack: Pack) => void;
  refreshPack: () => void;
}

const LinkPackModal = forwardRef<LinkPackModalRef, { onCreateLink?: (pack: Pack) => void }>(({ onCreateLink }, ref) => {
  const { t } = useTranslation('packsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmModalRef = useRef<any>(null);
  const { toast } = useToast();

  const [pack, setPack] = useState<Pack | null>(null);
  const [shops, setShops] = useState<{ value: string; label: string; shopObj?: any }[]>([]);

  // Cargar tiendas al montar
  useEffect(() => {
    const httpClient = createHttpClient(apiClientWH);
    const adapter = new ShopApiAdapter(httpClient);
    adapter.getAll().then((data) => {
      setShops(
        data.map((shop) => ({
          value: shop.id,
          label: shop.name,
          shopObj: shop,
        })),
      );
    });
  }, []);

  useImperativeHandle(ref, () => ({
    openWithPack: (packData: Pack) => {
      setPack(packData);
      modalRef.current?.openModal();
    },
    refreshPack: async () => {
      if (pack) {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new PackApiAdapter(httpClient);
        const updatedPack = await adapter.getPackById(pack.id);
        setPack(updatedPack);
      }
    },
  }));

  const handleCreateLink = () => {
    if (!pack) return;
    onCreateLink?.(pack);
  };

  const handleDeleteLink = (linkId: number) => {
    confirmModalRef.current?.open(async () => {
      try {
        if (!pack) return;
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new PackApiAdapter(httpClient);
        await adapter.deleteLink(linkId);

        toast({
          title: t('link.delete_success_title'),
          description: t('link.delete_success_description'),
          variant: 'success',
        });

        // Recargar el pack
        if (pack) {
          const updatedPack = await adapter.getPackById(pack.id);
          setPack(updatedPack);
        }
      } catch (error: any) {
        const data = error?.response?.data;
        const apiMessage =
          data?.message ||
          data?.error?.message ||
          (Array.isArray(data?.errors) && data.errors[0]?.message) ||
          data?.error_description ||
          error?.message;
        toast({
          title: t('link.delete_error_title'),
          description: apiMessage || t('link.delete_error_description'),
          variant: 'destructive',
        });
      }
    });
  };

  const enlaces = pack?.linked_packs || [];

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('link.title')}
        type="default"
        width="lg"
        className="w-full h-full sm:w-[700px] sm:h-auto"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-6 mt-1 pb-4">
            {/* Información del pack */}
            {pack && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium text-foreground">{pack.title}</p>
              </div>
            )}

            {/* Listado de enlaces existentes */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">Enlaces existentes ({enlaces.length})</h3>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateLink}
                  className="flex items-center gap-2"
                  data-testid="open-create-link-button"
                >
                  <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
                  {t('link.create_link')}
                </Button>
              </div>

              {/* Lista de enlaces */}
              <div className="w-full divide-y divide-lines border border-border rounded-lg">
                {enlaces.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-12">
                    <h3 className="text-lg font-medium text-title mb-2">{t('link.no_links_title')}</h3>
                    <p className="text-subtext">{t('link.no_links_description')}</p>
                  </div>
                ) : (
                  enlaces.map((enlace) => {
                    const shop = shops.find((s) => String(s.value) === String(enlace.shop_id));

                    return (
                      <div key={enlace.id} className="flex items-center p-4 w-full">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{enlace.title}</div>
                          <div className="flex items-center gap-2 mt-1 text-sm">
                            <Shop
                              name={shop?.label || 'Tienda'}
                              src={shop?.shopObj?.image || 'https://placehold.co/32x32/png'}
                            />
                          </div>
                        </div>
                        <div className="flex items-center">
                          <button
                            className="ml-4 py-2 px-1 text-icons hover:text-destructive transition-colors"
                            onClick={() => handleDeleteLink(enlace.id)}
                            data-testid={`delete-link-${enlace.id}`}
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Botón cerrar */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="close-link-modal-button"
            >
              {t('button.cancel')}
            </Button>
          </div>
        </div>
      </ModalCenter>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
});

LinkPackModal.displayName = 'LinkPackModal';

export default LinkPackModal;
